import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Check, Loader2, Plus, ChevronDown, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebouncedIngredientSearch } from '@/hooks/useDebouncedIngredientSearch';
import { getIngredientDetails } from '@/lib/apiService';
import { SearchableDropdown } from '@/components/shared/SearchableDropdown';

// No need for complex interface - just use API responses directly

interface IngredientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentIngredient: string;
  onReplaceIngredient: (
    oldIngredient: string,
    newIngredient: { id: string; name: string; units: string[]; provider: 'spoonacular' | 'internal' }
  ) => void;
  onAddIngredient: (
    ingredient: { id: string; name: string; units: string[]; provider: 'spoonacular' | 'internal' },
    quantity: number,
    unit: string
  ) => void | Promise<void>;
  isReplacing?: boolean;
  allergies?: string[];
  clientId?: string;
}

const normalizeIngredientUnits = (units: unknown): string[] => {
  if (!Array.isArray(units)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const rawUnit of units) {
    const unit = String(rawUnit ?? '').trim();
    if (!unit) continue;
    const key = unit.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(unit);
  }

  return normalized;
};


// Units will now come from the API response for each ingredient

const IngredientSearchModal: React.FC<IngredientSearchModalProps> = ({
  isOpen,
  onClose,
  currentIngredient,
  onReplaceIngredient,
  onAddIngredient,
  isReplacing = false,
  allergies = [],
  clientId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIngredientName, setSelectedIngredientName] = useState<string>('');
  const [selectedIngredientUnits, setSelectedIngredientUnits] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<string>('1');
  const [unit, setUnit] = useState<string>('');
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedIngredientIndex, setSelectedIngredientIndex] = useState<number | null>(null);
  const [selectedIngredientId, setSelectedIngredientId] = useState<string>('');
  const [selectedIngredientProvider, setSelectedIngredientProvider] = useState<'spoonacular' | 'internal'>('spoonacular');
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const manuallyClosedRef = useRef(false);
  const { formattedResults: formattedSearchResults, loading, error } = useDebouncedIngredientSearch(searchQuery, {
    allergies,
    clientId,
  });

  // Handle manual close from SearchableDropdown
  const handleCloseDropdown = useCallback(() => {
    manuallyClosedRef.current = true;
    setShowDropdown(false);
  }, []);

  // Auto-show dropdown when search results arrive or when loading (but not when we have selected ingredient)
  useEffect(() => {
    // Don't auto-show if dropdown was manually closed - check first
    if (manuallyClosedRef.current) {
      return;
    }
    
    // Don't show dropdown if we have a selected ingredient matching the query (prevents re-triggering after selection)
    if (selectedIngredientName && searchQuery === selectedIngredientName) {
      setShowDropdown(false);
      return;
    }
    
    if (searchQuery.trim() && (loading || formattedSearchResults.length > 0)) {
      setShowDropdown(true);
    } else if (!searchQuery.trim()) {
      setShowDropdown(false);
    }
  }, [formattedSearchResults, searchQuery, loading, selectedIngredientName]);

  // Reset manual close flag when user starts typing a different search (but not immediately)
  useEffect(() => {
    if (manuallyClosedRef.current && searchQuery.trim() !== '') {
      // When user types, clear the flag after a short delay to allow new searches
      const timeoutId = setTimeout(() => {
        manuallyClosedRef.current = false;
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setIsUnitDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectIngredient = async (
    item: {
      id?: string;
      name?: string;
      possibleUnits?: string[];
      source?: string;
      provider?: string;
      sourceId?: string;
      internalId?: string;
      spoonacularId?: string;
    } | string,
    index: number
  ) => {
    const ingredientName = typeof item === 'string' ? item : String(item?.name ?? '').trim();
    const rawIngredientId = typeof item === 'string' ? '' : String(item?.id ?? '').trim();
    const ingredientId = rawIngredientId.startsWith('ingredient-') ? '' : rawIngredientId;
    const sourceId = typeof item === 'string' ? '' : String(item?.sourceId ?? '').trim();
    const internalId = typeof item === 'string' ? '' : String(item?.internalId ?? '').trim();
    const spoonacularId = typeof item === 'string' ? '' : String(item?.spoonacularId ?? '').trim();
    const source = typeof item === 'string' ? '' : String(item?.source ?? '').toLowerCase();
    const provider = typeof item === 'string' ? '' : String(item?.provider ?? '').toLowerCase();
    const resolvedProvider: 'spoonacular' | 'internal' =
      provider === 'internal' || source === 'internal' || source === 'cached' || source === 'custom'
        ? 'internal'
        : 'spoonacular';
    const resolvedIngredientId = ingredientId || sourceId || internalId || spoonacularId;
    if (!ingredientName) return;

    setLoadingUnits(true);
    setSelectedIngredientIndex(index);
    // Set search query to show selected value, but prevent dropdown from showing
    setSearchQuery(ingredientName);
    setShowDropdown(false);
    const defaultUnit = 'g';
    setUnit(''); // Reset unit selection
    
    try {
      const detailsLookupId = ingredientId || resolvedIngredientId;
      const detailsResponse = detailsLookupId
        ? await getIngredientDetails(detailsLookupId, 1)
        : null;
      const unitsFromOptions = normalizeIngredientUnits(
        Array.isArray(detailsResponse?.unitOptions)
          ? detailsResponse.unitOptions.map((option) => option?.value ?? '')
          : []
      );
      const unitsFromDetails = normalizeIngredientUnits(detailsResponse?.units ?? []);
      const resolvedUnits = normalizeIngredientUnits([...unitsFromOptions, ...unitsFromDetails]);
      
      setSelectedIngredientName(ingredientName);
      setSelectedIngredientId(resolvedIngredientId || ingredientName);
      setSelectedIngredientProvider(resolvedProvider);
      setSelectedIngredientUnits(resolvedUnits.length > 0 ? resolvedUnits : [defaultUnit]);
      setQuantity('1');
      setUnit(resolvedUnits[0] || defaultUnit);
    } catch (err) {
      console.error('Error fetching units:', err);
      setSelectedIngredientName(ingredientName);
      setSelectedIngredientId(resolvedIngredientId || ingredientName);
      setSelectedIngredientProvider(resolvedProvider);
      setSelectedIngredientUnits([defaultUnit]);
      setQuantity('1');
      setUnit(defaultUnit);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleConfirmIngredient = async () => {
    if (selectedIngredientName && quantity && unit) {
      const numQuantity = parseFloat(quantity);
      if (numQuantity > 0) {
        const ingredient = {
          id: selectedIngredientId || selectedIngredientName,
          name: selectedIngredientName,
          units: selectedIngredientUnits,
          provider: selectedIngredientProvider,
        };
        
        if (isReplacing) {
          onReplaceIngredient(currentIngredient, ingredient);
          onClose();
          return;
        }

        try {
          setIsSubmitting(true);
          await Promise.resolve(onAddIngredient(ingredient, numQuantity, unit));
          onClose();
        } finally {
          setIsSubmitting(false);
        }
      }
    }
  };

  const handleClose = () => {
    setSearchQuery(''); // Clear search when closing
    setSelectedIngredientName('');
    setSelectedIngredientId('');
    setSelectedIngredientUnits([]);
    setSelectedIngredientIndex(null);
    setSelectedIngredientProvider('spoonacular');
    setQuantity('1');
    setUnit('');
    setIsUnitDropdownOpen(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            {isReplacing ? 'Replace Ingredient' : 'Add Ingredient'}
          </DialogTitle>
          {isReplacing && currentIngredient && (
            <div className="text-sm text-muted-foreground">
              Currently replacing: <Badge variant="outline">{currentIngredient}</Badge>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="space-y-2">
            <SearchableDropdown
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onSelect={(item: any) =>
                handleSelectIngredient(
                  item,
                  formattedSearchResults.findIndex((i) =>
                    (i.id && item?.id ? i.id === item.id : i.name === item?.name)
                  )
                )
              }
              searchResults={formattedSearchResults}
              isSearching={loading}
              showResults={showDropdown}
              onClose={handleCloseDropdown}
              placeholder="Search for ingredients..."
              inputClassName="bg-white h-11"
              colorPrefix="blue"
              searchResultsHeader="Ingredient Search Results"
              emptyStateText={loading ? "Searching..." : error ? error : "No ingredients found"}
              getItemId={(item) => item.id}
              getItemDisplayName={(item) => item.name}
            />
            {!searchQuery && (
              <p className="text-sm text-gray-500">
                💡 Start typing to search for ingredients like "milk", "chicken", "eggs"
              </p>
            )}
          </div>

          {/* Ingredient Suggestions - Compact buttons (optional, can be removed if using SearchableDropdown exclusively) */}
          {searchQuery && formattedSearchResults.length > 0 && selectedIngredientName && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">Or select another ingredient:</h3>
              <div className="max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {formattedSearchResults.map((item, index) => (
                    <button
                      key={item.id}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                        selectedIngredientIndex === index
                          ? 'bg-green-100 border-green-300 text-green-900'
                          : 'bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300 text-gray-700'
                      }`}
                      onClick={() => handleSelectIngredient(item, index)}
                    >
                      {selectedIngredientIndex === index && (
                        <Check className="h-3 w-3" />
                      )}
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Quantity and Unit Input - Show when ingredient is selected */}
          {(selectedIngredientName || loadingUnits) && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg space-y-4">
              {loadingUnits ? (
                <div className="flex items-center justify-center gap-3 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Loading available units...</span>
                </div>
              ) : selectedIngredientName ? (
                <>
                  <div className="flex items-center gap-2 p-2 bg-white rounded-md border border-green-200">
                    <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{selectedIngredientName}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="quantity" className="text-xs font-semibold text-gray-700">Quantity</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="1"
                        className="h-9 text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="unit" className="text-xs font-semibold text-gray-700">Unit</Label>
                      <div className="relative" ref={unitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                          className="flex h-9 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-all duration-200 hover:border-blue-400 hover:shadow-sm"
                        >
                          <span className={unit ? 'text-gray-900' : 'text-gray-500'}>
                            {unit || 'Select unit'}
                          </span>
                          <ChevronDown 
                            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                              isUnitDropdownOpen ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                        
                        {isUnitDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                            {selectedIngredientUnits.length > 0 ? (
                              selectedIngredientUnits.map((unitOption) => (
                                <button
                                  key={unitOption}
                                  type="button"
                                  onClick={() => {
                                    setUnit(unitOption);
                                    setIsUnitDropdownOpen(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors duration-150 ${
                                    unit === unitOption 
                                      ? 'bg-blue-100 text-blue-900 font-medium' 
                                      : 'text-gray-900'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span>{unitOption}</span>
                                    {unit === unitOption && (
                                      <Check className="h-4 w-4 text-blue-600" />
                                    )}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No units available
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Validation feedback */}
                  {quantity && parseFloat(quantity) <= 0 && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 p-2 rounded-md">
                      <X className="h-3 w-3" />
                      Quantity must be greater than 0
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleConfirmIngredient}
                      disabled={isSubmitting || !quantity || !unit || parseFloat(quantity) <= 0}
                      className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3 mr-1" />
                      )}
                      {isSubmitting ? 'Saving...' : isReplacing ? 'Replace' : 'Add'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSelectedIngredientName('');
                        setSelectedIngredientId('');
                        setSelectedIngredientUnits([]);
                        setSelectedIngredientIndex(null);
                      }}
                      className="h-9 px-3 text-sm border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IngredientSearchModal;
